import { CapaDeModal } from '@/shared/components/CapaDeModal'
import { useState, useEffect, useCallback } from 'react'
import { X, CheckCheck, MessageSquare, AlertTriangle, Loader2, FileText, CalendarDays } from 'lucide-react'
import { fileService } from '@/services/fileService'
import RequestFinancialDocsModal from '@/features/proveedores/components/RequestFinancialDocsModal'
import {
  getFinancialDocuments,
  requestFinancialDocuments,
  setBloqueoDocsFinancieros,
  reviewFinancialDocument,
  estadoDocumento,
  errorMessage,
  fmtFecha,
  aFechaInput,
  ESTADO_META,
  type FinancialDocsState,
  type FinancialDocRequirement,
} from '@/features/proveedores/api/supplierFinancialDocs.service'
import { Badge } from '@/shared/components/Badge'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'

// Tab "Documentos financieros" del detalle interno del proveedor
// (C-NEW-02 / SP-210), conectado al back de SP-211:
// POST  /internal/suppliers/:id/financial-documents/request   → solicitar
// GET   /internal/suppliers/:id/financial-documents           → estado
// PATCH /internal/suppliers/:id/bloqueo-docs-financieros      → toggle
// POST  /internal/suppliers/:id/documents/:documentId/review  → aprobar/rechazar
//                                                              y corregir fecha

interface FinancialDocsSectionProps {
  supplierId: string
  /** El botón del encabezado abre el modal de solicitud desde afuera. */
  requestModalOpen?: boolean
  onCloseRequestModal?: () => void
}

/** Qué está haciendo Contraloría con el documento del modal abierto. */
type AccionRevision = 'aprobar' | 'rechazar' | 'fecha'

const TITULO_ACCION: Record<AccionRevision, string> = {
  aprobar: 'Aprobar documento',
  rechazar: 'Rechazar documento',
  fecha: 'Corregir fecha de vencimiento',
}

const FinancialDocsSection = ({
  supplierId,
  requestModalOpen = false,
  onCloseRequestModal,
}: FinancialDocsSectionProps) => {
  const [state, setState] = useState<FinancialDocsState | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [solicitando, setSolicitando] = useState(false)
  const [guardandoBloqueo, setGuardandoBloqueo] = useState(false)
  const [accionError, setAccionError] = useState<string | null>(null)
  // Documento en proceso de aprobación/rechazo, para deshabilitar sus botones.
  const [revisandoId, setRevisandoId] = useState<string | null>(null)
  // Aprobar, rechazar y corregir fecha comparten modal: los tres terminan en la
  // misma llamada de revisión, solo cambia qué se manda.
  const [accion, setAccion] = useState<
    { req: FinancialDocRequirement; tipo: AccionRevision } | null
  >(null)
  const [comentarioRechazo, setComentarioRechazo] = useState('')
  const [fecha, setFecha] = useState('')
  const [verComentario, setVerComentario] = useState<FinancialDocRequirement | null>(null)

  const load = useCallback(async () => {
    if (!supplierId) return
    setLoading(true)
    setLoadError(null)
    try {
      setState(await getFinancialDocuments(supplierId))
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setLoadError(errorMessage(err, 'No se pudo cargar el estado de los documentos financieros.'))
    } finally {
      setLoading(false)
    }
  }, [supplierId])

  useEffect(() => {
    load()
  }, [load])

  const documentos = state?.documents ?? []
  const bloqueo = state?.bloqueoDocsFinancieros ?? false

  const handleSolicitar = async () => {
    setSolicitando(true)
    setAccionError(null)
    try {
      await requestFinancialDocuments(supplierId)
      // Se relee para traer la lista de documentos ya con la solicitud activa.
      await load()
      onCloseRequestModal?.()
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setAccionError(errorMessage(err, 'No se pudo enviar la solicitud.'))
    } finally {
      setSolicitando(false)
    }
  }

  const handleToggleBloqueo = async () => {
    if (!state || guardandoBloqueo) return
    const nuevo = !bloqueo
    setGuardandoBloqueo(true)
    setAccionError(null)
    // Optimista: el switch responde al momento y se revierte si el PATCH falla.
    setState({ ...state, bloqueoDocsFinancieros: nuevo })
    try {
      await setBloqueoDocsFinancieros(supplierId, nuevo)
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setState({ ...state, bloqueoDocsFinancieros: !nuevo })
      setAccionError(errorMessage(err, 'No se pudo actualizar el bloqueo de facturación.'))
    } finally {
      setGuardandoBloqueo(false)
    }
  }

  const abrirAccion = (req: FinancialDocRequirement, tipo: AccionRevision) => {
    setAccion({ req, tipo })
    setComentarioRechazo('')
    // Se parte de lo que capturó el proveedor: Contraloría corrige, no recaptura.
    setFecha(aFechaInput(req.latestDocument?.validUntil))
  }

  const cerrarAccion = () => {
    setAccion(null)
    setComentarioRechazo('')
    setFecha('')
  }

  const confirmarAccion = async () => {
    const doc = accion?.req.latestDocument
    if (!accion || !doc) return
    const { tipo } = accion
    if (tipo === 'rechazar' && comentarioRechazo.trim() === '') return

    // Corregir solo la fecha reenvía el estatus que ya tenía el documento, y su
    // comentario: el back reasigna `reviewComment` en cada revisión, así que no
    // mandarlo borraría el motivo del rechazo.
    const status =
      tipo === 'aprobar' || (tipo === 'fecha' && doc.status === 'approved')
        ? 'approved'
        : 'rejected'
    const comentario =
      tipo === 'rechazar' ? comentarioRechazo.trim() : doc.reviewComment ?? undefined

    setRevisandoId(doc.id)
    setAccionError(null)
    try {
      // La fecha va siempre: vacía se manda null y el back la borra, que es la
      // forma de deshacer una captura equivocada.
      await reviewFinancialDocument(supplierId, doc.id, status, comentario, fecha || null)
      cerrarAccion()
      await load()
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      const falla = {
        aprobar: 'No se pudo aprobar el documento.',
        rechazar: 'No se pudo rechazar el documento.',
        fecha: 'No se pudo actualizar la fecha de vencimiento.',
      }[tipo]
      setAccionError(errorMessage(err, falla))
    } finally {
      setRevisandoId(null)
    }
  }

  const verDocumento = async (fileUrl: string) => {
    try {
      const blobUrl = await fileService.getViewableFileUrl(fileUrl)
      window.open(blobUrl, '_blank')
      setTimeout(() => fileService.revokeViewableFileUrl(blobUrl), 30000)
    } catch {
      window.open(fileUrl, '_blank')
    }
  }

  const modalSolicitud = (
    <RequestFinancialDocsModal
      open={requestModalOpen}
      docs={documentos.map((d) => d.documentType.name)}
      onClose={() => { if (!solicitando) onCloseRequestModal?.() }}
      onConfirm={handleSolicitar}
      loading={solicitando}
    />
  )

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-8 flex items-center justify-center gap-2 text-sm text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" />
        Cargando documentos financieros…
      </div>
    )
  }

  if (loadError) {
    return (
      <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
        <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-red-800">
          <p>{loadError}</p>
          <button onClick={load} className="mt-1 font-medium underline hover:no-underline">
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {accionError && (
        <div className="flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{accionError}</p>
        </div>
      )}

      {/* Header: solicitados + toggle bloquear facturación */}
      <div className="bg-white rounded-lg border border-[#E5E7EB] p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            Documentos financieros solicitados
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {state?.requestedAt
              ? `Solicitado el ${fmtFecha(state.requestedAt)}`
              : 'Todavía no se han solicitado a este proveedor.'}
          </p>
        </div>
        <label className="flex items-center gap-3 cursor-pointer select-none">
          <span className="text-sm text-gray-700">
            Bloquear facturación hasta aprobación
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={bloqueo}
            disabled={guardandoBloqueo}
            onClick={handleToggleBloqueo}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-60 ${bloqueo ? 'bg-[#2563EB]' : 'bg-gray-300'}`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${bloqueo ? 'translate-x-[22px]' : 'translate-x-0.5'}`}
            />
          </button>
        </label>
      </div>

      {/* Tabla de documentos financieros */}
      <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
        {documentos.length === 0 ? (
          <p className="px-5 py-8 text-sm text-gray-500 text-center">
            No hay documentos financieros configurados para este tipo de proveedor.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-[#E5E7EB]">
              <thead className="bg-[#FAF9FF]">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Documento</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider whitespace-nowrap">Fecha de subida</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider whitespace-nowrap">Fecha de vencimiento</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Documento</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Estatus</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-[#E1E1E1]">
                {documentos.map((req, i) => {
                  const doc = req.latestDocument
                  const estado = estadoDocumento(doc)
                  const meta = ESTADO_META[estado]
                  const enCurso = !!doc && revisandoId === doc.id
                  const canReview = estado === 'subido' && !enCurso
                  return (
                    <tr key={req.documentType.id} className="hover:bg-[#F4F7FB] transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {i + 1}. {req.documentType.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {fmtFecha(doc?.uploadedAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {fmtFecha(doc?.validUntil)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {doc ? (
                          <button
                            onClick={() => verDocumento(doc.fileUrl)}
                            className="inline-flex items-center gap-1.5 text-[#315FB9] hover:underline text-left"
                          >
                            <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            {doc.fileName}
                          </button>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge color={meta.color}>{meta.label}</Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {estado === 'subido' && (
                            <>
                              <button
                                disabled={!canReview}
                                onClick={() => abrirAccion(req, 'aprobar')}
                                className={estiloDeBoton('exito')}
                              >
                                Aprobar
                                {enCurso ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCheck className="w-4 h-4" />}
                              </button>
                              <button
                                disabled={!canReview}
                                onClick={() => abrirAccion(req, 'rechazar')}
                                className={estiloDeBoton('peligro')}
                              >
                                Rechazar
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {estado === 'rechazado' && (
                            <button
                              onClick={() => setVerComentario(req)}
                              className="inline-flex items-center gap-1.5 text-[#315FB9] hover:underline text-sm font-medium"
                            >
                              <MessageSquare className="w-4 h-4" />
                              Ver comentario
                            </button>
                          )}
                          {/* La fecha se corrige en cualquier documento ya subido,
                              esté revisado o no. */}
                          {doc && estado !== 'subido' && (
                            <button
                              disabled={!!revisandoId}
                              onClick={() => abrirAccion(req, 'fecha')}
                              className={estiloDeBoton('secundario')}
                            >
                              <CalendarDays className="w-4 h-4" />
                              Corregir fecha
                            </button>
                          )}
                          {!doc && <span className="text-gray-400 text-sm">—</span>}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de revisión: aprobar, rechazar o solo corregir la fecha */}
      {accion && (
        <CapaDeModal zIndex={60}>
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <button
              onClick={cerrarAccion}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-bold text-gray-900 pr-6">
              {TITULO_ACCION[accion.tipo]}
            </h2>
            <p className="text-sm text-gray-500 mt-1">{accion.req.documentType.name}</p>

            {accion.tipo === 'rechazar' && (
              <>
                <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">
                  Motivo del rechazo <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={comentarioRechazo}
                  onChange={(e) => setComentarioRechazo(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2.5 rounded-lg text-sm text-gray-800 bg-[#F6F9FB] border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Explica al proveedor qué debe corregir"
                />
              </>
            )}

            <label className="block text-sm font-medium text-gray-700 mt-4 mb-1">
              Fecha de vencimiento
            </label>
            <input
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg text-sm text-gray-800 bg-[#F6F9FB] border-0 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              {accion.tipo === 'fecha'
                ? 'Al guardar, el proveedor recibirá de nuevo la notificación de este documento.'
                : 'La capturó el proveedor al subir. Déjala vacía para borrarla.'}
            </p>

            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={cerrarAccion}
                className={estiloDeBoton('secundario')}
              >
                Cancelar
              </button>
              <button
                onClick={confirmarAccion}
                disabled={
                  !!revisandoId ||
                  (accion.tipo === 'rechazar' && comentarioRechazo.trim() === '')
                }
                className={estiloDeBoton('primario')}
              >
                {revisandoId
                  ? 'Guardando…'
                  : { aprobar: 'Aprobar', rechazar: 'Rechazar', fecha: 'Guardar' }[accion.tipo]}
              </button>
            </div>
          </div>
        </CapaDeModal>
      )}

      {/* Modal: ver comentario del rechazo */}
      {verComentario && (
        <CapaDeModal zIndex={60}>
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <button
              onClick={() => setVerComentario(null)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-lg font-bold text-gray-900 pr-6">Motivo del rechazo</h2>
            <p className="text-sm text-gray-500 mt-1">{verComentario.documentType.name}</p>
            <p className="text-sm text-gray-700 mt-4 bg-[#F6F9FB] rounded-lg p-3">
              {verComentario.latestDocument?.reviewComment || 'Sin comentario.'}
            </p>
          </div>
        </CapaDeModal>
      )}

      {modalSolicitud}
    </div>
  )
}

export default FinancialDocsSection
